import React, { useEffect, useMemo, useState } from "react";
import { calculateAnalysisTotals } from "../utils/analysisTotals.js";
import { useTranslation } from "react-i18next";
import {
  fetchCorporateDaily,
  fetchCorporateDailyLocations,
  fetchSiteConfigs,
  fetchFuelTypes,
} from "../api/client";

const ECO_FUEL_TYPE_ID = "burning_oil_eco";
const localeCompareFi = (a = "", b = "") =>
  a.localeCompare(b, "fi", { sensitivity: "base" });

const PROVIDER_LABELS = {
  neste_corp: "Neste",
  st1_for_corporate: "St1",
  abc_corp: "ABC",
};

function formatProductName(item) {
  const baseName =
    item.product_name || item.product_code || item.product_group || "Tuote";
  if (
    (item.fuelTypeId && item.fuelTypeId === ECO_FUEL_TYPE_ID) ||
    /eco-öljy/i.test(item.fuelTypeLabel || "")
  ) {
    return /hvo/i.test(baseName) ? baseName : `${baseName} (RE HVO)`;
  }
  return baseName;
}

function normalizeRows(items = []) {
  return items
    .map((item, idx) => {
      const provider = item.provider || "unknown";
      const location =
        item.postal_display_name ||
        item.municipality ||
        item.location ||
        "Unknown";
      const postalCode =
        item.postal_display_code ||
        item.postal_code ||
        "";
      const locationKey =
        location && postalCode
          ? `${location} (${postalCode})`
          : location || postalCode || `LOC-${idx}`;
      const productName = formatProductName(item);
      const basePrice =
        typeof item.unit_price === "number"
          ? item.unit_price
          : typeof item.unit_price_excl === "number"
          ? item.unit_price_excl
          : typeof item.unit_price_incl === "number"
          ? item.unit_price_incl
          : null;

      if (basePrice === null) return null;

      return {
        id: `${provider}-${location}-${productName}-${item.product_code || idx}`,
        provider,
        providerLabel: PROVIDER_LABELS[provider] || provider,
        location,
        locationName: location,
        locationCode: postalCode,
        locationKey,
        productName,
        productGroup: item.product_group || item.product_category || "",
        basePrice,
        priceIncl:
          typeof item.unit_price_incl === "number"
            ? item.unit_price_incl
            : null,
        categoryId: item.categoryId || null,
        categoryLabel: item.categoryLabel || null,
        fuelTypeId: item.fuelTypeId || null,
        fuelTypeLabel: item.fuelTypeLabel || null,
      };
    })
    .filter(Boolean);
}

function findTier(tiers = [], litres = 0) {
  if (!Array.isArray(tiers)) return null;
  return tiers.find((tier) => litres >= tier.min && litres <= tier.max);
}

export default function AnalysisPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [siteConfig, setSiteConfig] = useState({});
  const [error, setError] = useState(null);

  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedLocationLabels, setSelectedLocationLabels] = useState({});
  const [locations, setLocations] = useState([]);
  const [locationSearchInput, setLocationSearchInput] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [showScheduledLocations, setShowScheduledLocations] = useState(true);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(true);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedFuelType, setSelectedFuelType] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [fuelCategories, setFuelCategories] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [sortKey, setSortKey] = useState("totalInclVAT");
  const [sortDir, setSortDir] = useState("desc");
  const [analysisLitres, setAnalysisLitres] = useState(1000);
  const [overrideDiscount, setOverrideDiscount] = useState("");
  const [overrideProfit, setOverrideProfit] = useState("");
  const [overrideFastDelivery, setOverrideFastDelivery] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [config, categories] = await Promise.all([
          fetchSiteConfigs(),
          fetchFuelTypes().catch(() => []),
        ]);
        setSiteConfig(config || {});
        setFuelCategories(
          (categories || []).map((group) => ({
            id: group.id,
            label: group.label,
          }))
        );
        const variants = [];
        (categories || []).forEach((group) => {
          group.variants?.forEach((variant) => {
            variants.push({
              id: variant.id,
              label: `${group.label} – ${variant.label}`,
              categoryId: group.id,
            });
          });
        });
        setFuelTypes(variants);
      } catch (err) {
        console.error("Error loading corporate analysis data:", err);
        setError(t("analysis.error"));
      }
    };
    load();
  }, [t]);

  useEffect(() => {
    const timer = window.setTimeout(() => setLocationQuery(locationSearchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [locationSearchInput]);

  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setIsLoadingLocations(true);
    fetchCorporateDailyLocations({ query: locationQuery || undefined, scope: showScheduledLocations && !locationQuery ? "scheduled" : undefined, signal: controller.signal })
      .then((response) => {
        if (!current) return;
        const options = response.recommended && !response.items.some((item) => item.id === response.recommended.id) ? [response.recommended, ...response.items] : response.items;
        setLocations(options);
      })
      .catch((err) => { if (err.code !== "ERR_CANCELED" && current) { setError(err.response?.data?.message || t("analysis.errors.locations")); setLocations([]); } })
      .finally(() => { if (current) setIsLoadingLocations(false); });
    return () => { current = false; controller.abort(); };
  }, [showScheduledLocations, locationQuery, t]);

  useEffect(() => {
    if (selectedLocations.length === 0) { setRows([]); return undefined; }
    const controller = new AbortController();
    let current = true;
    setIsLoadingData(true);
    fetchCorporateDaily({ locations: selectedLocations, providers: selectedProvider ? [selectedProvider] : [], signal: controller.signal })
      .then((daily) => { if (current) { setRows(normalizeRows(daily.items)); setError(null); } })
      .catch((err) => { if (err.code !== "ERR_CANCELED" && current) { setError(err.response?.data?.message || t("analysis.error")); setRows([]); } })
      .finally(() => { if (current) setIsLoadingData(false); });
    return () => { current = false; controller.abort(); };
  }, [selectedLocations, selectedProvider, t]);

  const providers = Object.keys(PROVIDER_LABELS);
  const products = useMemo(
    () => [...new Set(rows.map((r) => r.productName))],
    [rows]
  );
  const litresValue =
    Number.isFinite(analysisLitres) && analysisLitres > 0 ? analysisLitres : 1000;

  const availableFuelTypes = useMemo(() => {
    if (!selectedCategory) return fuelTypes;
    return fuelTypes.filter((type) => type.categoryId === selectedCategory);
  }, [fuelTypes, selectedCategory]);

  useEffect(() => {
    if (
      selectedFuelType &&
      !availableFuelTypes.some((type) => type.id === selectedFuelType)
    ) {
      setSelectedFuelType("");
    }
  }, [availableFuelTypes, selectedFuelType]);

  const availableProducts = useMemo(() => {
    const names = new Set();
    rows.forEach((row) => {
      if (selectedCategory && row.categoryId !== selectedCategory) return;
      if (selectedFuelType && row.fuelTypeId !== selectedFuelType) return;
      names.add(row.productName);
    });

    if (names.size === 0) {
      return [...products].sort((a, b) => localeCompareFi(a, b));
    }
    return Array.from(names).sort((a, b) => localeCompareFi(a, b));
  }, [rows, products, selectedCategory, selectedFuelType]);

  useEffect(() => {
    if (selectedProduct && !availableProducts.includes(selectedProduct)) {
      setSelectedProduct("");
    }
  }, [availableProducts, selectedProduct]);

  const enriched = useMemo(() => {
    return rows.map((row) => {
      const cfg = siteConfig[row.provider] || {};
      const normalizedLitres = litresValue;
      const discountTier = findTier(cfg.discounts, normalizedLitres);
      const deliveryTier = findTier(cfg.delivery_prices, normalizedLitres);

      const discountPerUnit =
        overrideDiscount !== "" ? Number(overrideDiscount) : Number(discountTier?.discount ?? 0);
      const deliveryTotal = Number(
        deliveryTier?.delivery_price ?? cfg?.delivery_price ?? 0
      );
      const fastDeliveryTotal =
        overrideFastDelivery !== "" ? Number(overrideFastDelivery) : Number(cfg?.fast_delivery_price ?? 0);
      const profitPerUnit =
        overrideProfit !== "" ? Number(overrideProfit) : Number(cfg?.profit ?? 0);
      const vatRate = Number(cfg?.vat ?? 0);

      const { baseTotal, profitTotal, totalExclVAT, totalInclVAT } = calculateAnalysisTotals({
        basePrice: row.basePrice, litres: normalizedLitres, discountPerUnit,
        profitPerUnit, deliveryTotal, fastDeliveryTotal, vatRate,
      });

      return {
        ...row,
        litres: normalizedLitres,
        discountPerUnit,
        profitPerUnit,
        deliveryTotal,
        fastDeliveryTotal,
        profitTotal,
        baseTotal,
        totalExclVAT,
        totalInclVAT,
        vatRate,
      };
    });
  }, [
    rows,
    siteConfig,
    litresValue,
    overrideDiscount,
    overrideProfit,
    overrideFastDelivery,
  ]);

  const filtered = useMemo(() => {
    return enriched.filter((row) => {
      const locationMatch = selectedLocations.length > 0
        ? selectedLocations.includes(row.locationCode) || selectedLocations.includes(row.locationKey)
        : true;
      const providerMatch = selectedProvider
        ? row.provider === selectedProvider
        : true;
      const productMatch = selectedProduct
        ? row.productName === selectedProduct
        : true;
      const categoryMatch = selectedCategory
        ? row.categoryId === selectedCategory
        : true;
      const fuelTypeMatch = selectedFuelType
        ? row.fuelTypeId === selectedFuelType
        : true;
      return (
        locationMatch &&
        providerMatch &&
        productMatch &&
        categoryMatch &&
        fuelTypeMatch
      );
    });
  }, [
    enriched,
    selectedLocations,
    selectedProvider,
    selectedProduct,
    selectedCategory,
    selectedFuelType,
  ]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (sortKey === "location") {
        const locationOrder = localeCompareFi(a.locationName || a.location, b.locationName || b.location);
        const order = locationOrder || localeCompareFi(a.locationCode, b.locationCode);
        return sortDir === "asc" ? order : -order;
      }
      const valA = a[sortKey];
      const valB = b[sortKey];
      if (valA === valB) return 0;
      if (sortDir === "asc") {
        return valA > valB ? 1 : -1;
      }
      return valA < valB ? 1 : -1;
    });
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const showScheduledLocationList = () => {
    setLocationSearchInput("");
    setLocationQuery("");
    setShowScheduledLocations(true);
    setShowLocationSuggestions(true);
  };
  const showAllLocationList = () => {
    setLocationSearchInput("");
    setLocationQuery("");
    setShowScheduledLocations(false);
    setShowLocationSuggestions(true);
  };
  const toggleLocation = (location) => {
    const locationId = typeof location === "string" ? location : location.id;
    setSelectedLocations((previous) =>
      previous.includes(locationId)
        ? previous.filter((id) => id !== locationId)
        : [...previous, locationId]
    );
    setSelectedLocationLabels((previous) => {
      if (typeof location === "string" || previous[locationId]) return previous;
      return { ...previous, [locationId]: location.label };
    });
  };
  const clearLocationSelection = () => {
    setSelectedLocations([]);
    setSelectedLocationLabels({});
    setLocationSearchInput("");
    setLocationQuery("");
    setShowScheduledLocations(true);
    setShowLocationSuggestions(true);
  };

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-xl font-bold mb-4">{t("analysis.title")}</h2>
      {error && <p className="text-red-500">{error}</p>}

      <div className="bg-white p-4 rounded-xl shadow space-y-4">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={showScheduledLocationList} className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 text-sm">
            {t("analysis.locations.scheduled")}
          </button>
          <button type="button" onClick={showAllLocationList} className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 text-sm">
            {t("analysis.locations.all")}
          </button>
          <button type="button" onClick={clearLocationSelection} className="bg-gray-300 text-gray-800 px-3 py-2 rounded hover:bg-gray-400 text-sm">
            {t("common.clear")}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            {t("analysis.location")}
          </label>
          <input
            type="search"
            value={locationSearchInput}
            onFocus={() => setShowLocationSuggestions(true)}
            onChange={(event) => { setLocationSearchInput(event.target.value); setShowScheduledLocations(false); setShowLocationSuggestions(true); }}
            placeholder={t("analysis.locations.searchPlaceholder")}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <p className="block text-sm font-medium text-gray-600 mb-1">
            {t("analysis.locations.choose")}
          </p>
          {isLoadingLocations && <p className="text-sm text-gray-500 py-2">{t("common.loading")}</p>}
          {showLocationSuggestions && !isLoadingLocations && (
            <div className="max-h-48 overflow-y-auto border rounded-lg bg-white">
              {locations.map((loc) => (
                <label key={loc.id} className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100">
                  <input type="checkbox" checked={selectedLocations.includes(loc.id)} onChange={() => toggleLocation(loc)} />
                  <span>{loc.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="block text-sm font-medium text-gray-600 mb-1">
            {t("analysis.locations.selected")}
          </p>
          <div className="min-h-10 rounded-lg border bg-gray-50 p-2">
            {selectedLocations.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {selectedLocations.map((id) => (
                  <button key={id} type="button" onClick={() => toggleLocation(id)} className="rounded bg-blue-100 px-2 py-1 text-left text-xs text-blue-900 hover:bg-blue-200">
                    {selectedLocationLabels[id] || id} ×
                  </button>
                ))}
              </div>
            ) : (
              <p className="py-1 text-sm text-gray-500">{t("analysis.locations.noneSelected")}</p>
            )}
          </div>
        </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-8 gap-2 [&>div>label]:mb-1 [&>div>label]:text-xs [&>div>input]:px-2 [&>div>input]:py-1.5 [&>div>input]:text-sm [&>div>select]:px-2 [&>div>select]:py-1.5 [&>div>select]:text-sm">
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            {t("analysis.provider")}
          </label>
          <select
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
          >
            <option value="">{t("common.all")}</option>
            {providers.map((prov) => (
              <option key={prov} value={prov}>
                {PROVIDER_LABELS[prov] || prov}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            {t("analysis.product")}
          </label>
          <select
            value={selectedProduct}
            onChange={(e) => setSelectedProduct(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
          >
            <option value="">{t("common.all")}</option>
            {availableProducts.map((product) => (
              <option key={product} value={product}>
                {product}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            {t("analysis.fuelType")}
          </label>
          <select
            value={selectedFuelType}
            onChange={(e) => setSelectedFuelType(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
          >
            <option value="">{t("common.all")}</option>
            {availableFuelTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            {t("analysis.fuelCategory")}
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
          >
            <option value="">{t("common.all")}</option>
            {fuelCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            {t("analysis.litres")}
          </label>
          <input
            type="number"
            min="1"
            value={analysisLitres}
            onChange={(e) => setAnalysisLitres(Number(e.target.value) || 0)}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            {t("analysis.discount")}
          </label>
          <input
            type="number"
            step="0.001"
            value={overrideDiscount}
            onChange={(e) => setOverrideDiscount(e.target.value)}
            placeholder={t("analysis.defaultValue")}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            {t("analysis.profit")}
          </label>
          <input
            type="number"
            step="0.001"
            value={overrideProfit}
            onChange={(e) => setOverrideProfit(e.target.value)}
            placeholder={t("analysis.defaultValue")}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            {t("analysis.fastDelivery")}
          </label>
          <input
            type="number"
            step="1.0"
            value={overrideFastDelivery}
            onChange={(e) => setOverrideFastDelivery(e.target.value)}
            placeholder={t("analysis.defaultValue")}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>
        </div>
      </div>

      <div className="bg-white border rounded-xl shadow p-4 overflow-x-auto">
        {isLoadingData && <p className="text-sm text-gray-500 mb-2">{t("common.loading")}</p>}
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              {[
                { key: "location", label: t("analysis.table.location") },
                { key: "providerLabel", label: t("analysis.table.provider") },
                { key: "productName", label: t("analysis.table.product") },
                { key: "categoryLabel", label: t("analysis.table.category") },
                { key: "fuelTypeLabel", label: t("analysis.table.fuelType") },
                { key: "litres", label: t("analysis.table.litres") },
                { key: "basePrice", label: t("analysis.table.basePrice") },
                { key: "discountPerUnit", label: t("analysis.table.discount") },
                { key: "profitPerUnit", label: t("analysis.table.profit") },
                { key: "deliveryTotal", label: t("analysis.table.delivery") },
                { key: "fastDeliveryTotal", label: t("analysis.table.fastDelivery") },
                { key: "totalExclVAT", label: t("analysis.table.totalExclVat") },
                { key: "totalInclVAT", label: t("analysis.table.totalInclVat") },
              ].map((col) => (
                <th
                  key={col.key}
                  className="text-left px-3 py-2 cursor-pointer select-none"
                  onClick={() => toggleSort(col.key)}
                >
                  {col.label}{" "}
                  <span className="text-gray-500">
                    {sortKey === col.key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length > 0 ? (
              sorted.map((row) => (
                <tr key={row.id} className="border-b last:border-0">
                  <td className="px-3 py-2">
                    <div className="font-medium">
                      {row.locationName || row.location}
                    </div>
                    {(row.locationCode || row.postal_display_code) && (
                      <div className="text-xs text-gray-500">
                        {row.locationCode || row.postal_display_code}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">{row.providerLabel}</td>
                  <td className="px-3 py-2">{row.productName}</td>
                  <td className="px-3 py-2">{row.categoryLabel || "—"}</td>
                  <td className="px-3 py-2">{row.fuelTypeLabel || "—"}</td>
                  <td className="px-3 py-2 text-right">{row.litres}</td>
                  <td className="px-3 py-2 text-right">
                    {row.basePrice.toFixed(4)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.discountPerUnit.toFixed(3)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.profitPerUnit.toFixed(3)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.deliveryTotal.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.fastDeliveryTotal.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.totalExclVAT.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.totalInclVAT.toFixed(2)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="text-center text-gray-500 py-4">
                  {t("analysis.noRows")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
